import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuoteTextTemplate } from '../../models';
import type { QuoteTextTemplateKind } from '../../models/QuoteTextTemplate';
import { QuoteTextTemplates } from '../../services';
import { Alert, Button, Confirm, Icon, Wysiwyg } from '..';

export type QuoteTextTemplatesManagerProps = {
    className?: string;
};

function QuoteTextTemplatesManager(props: QuoteTextTemplatesManagerProps) {
    const { className } = props;
    const { t } = useTranslation();

    const [kind, setKind] = useState<QuoteTextTemplateKind>('intro');
    const [templates, setTemplates] = useState<QuoteTextTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [activeId, setActiveId] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<string | null>(null);
    const [draftName, setDraftName] = useState('');
    const [draftBody, setDraftBody] = useState('');
    const [draftIsDefault, setDraftIsDefault] = useState(false);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const json = await QuoteTextTemplates.list({ kind });
            setTemplates(json.quote_text_templates || []);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('quote_text_templates.load_failed') || ''));
        } finally {
            setLoading(false);
        }
    }, [kind, t]);

    useEffect(() => {
        setActiveId(null);
        load();
    }, [load]);

    const openEdit = (template: QuoteTextTemplate) => {
        if (activeId === template._id) {
            setActiveId(null);
            return;
        }
        setActiveId(template._id);
        setDraftName(template.name);
        setDraftBody(template.body || '');
        setDraftIsDefault(!!template.isDefault);
        setError('');
    };

    const saveActive = async () => {
        if (!activeId)
            return;
        const name = draftName.trim();
        if (!name) {
            setError(t('quote_text_templates.name_required') || '');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const json = await QuoteTextTemplates.update({
                _id: activeId,
                name,
                kind,
                body: draftBody,
                isDefault: draftIsDefault,
            }, /*patch*/ false);
            setTemplates(prev => prev.map(item => (
                item._id === activeId ? json.quote_text_template : item
            )));
            // Refresh so unique-default flag is consistent across the list
            await load();
            setActiveId(null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('quote_text_templates.save_failed') || ''));
        } finally {
            setSaving(false);
        }
    };

    const createTemplate = async () => {
        const name = newName.trim();
        if (!name) {
            setError(t('quote_text_templates.name_required') || '');
            return;
        }
        setCreating(true);
        setError('');
        try {
            const json = await QuoteTextTemplates.create({
                name,
                kind,
                body: `<p>${t('quote_text_templates.default_body') || ''}</p>`,
                isDefault: templates.length === 0,
            });
            setNewName('');
            await load();
            openEdit(json.quote_text_template);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('quote_text_templates.save_failed') || ''));
        } finally {
            setCreating(false);
        }
    };

    const removeTemplate = async () => {
        if (!toDelete)
            return;
        try {
            await QuoteTextTemplates.delete(toDelete);
            setTemplates(prev => prev.filter(item => item._id !== toDelete));
            if (activeId === toDelete)
                setActiveId(null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : (t('quote_text_templates.delete_failed') || ''));
        } finally {
            setToDelete(null);
        }
    };

    return (
        <div className={`quote-text-templates-manager ${className || ''}`}>
            <h2 className="mt-1 mb-1 text-base font-semibold text-lg text-gray-900 dark:text-gray-100">
                {t('quote_text_templates.title')}
            </h2>
            <p className="mb-3 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300">
                {t('quote_text_templates.desc')}
            </p>

            <div className="mb-4 flex gap-2">
                {(['intro', 'presentation', 'conditions'] as QuoteTextTemplateKind[]).map((k) => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setKind(k)}
                        className={`px-3 py-1.5 text-sm rounded border ${
                            kind === k
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                        }`}
                    >
                        {t(`quote_text_templates.kind_${k}`)}
                    </button>
                ))}
            </div>

            {error && (
                <div className="mb-3">
                    <Alert title={error} icon="warning" color="failure" />
                </div>
            )}

            {loading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Icon type="loading" />
                    <span>{t('common.loading')}</span>
                </div>
            ) : (
                <div className="flex flex-col space-y-3">
                    {templates.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('quote_text_templates.empty')}</p>
                    )}
                    {templates.map((template: QuoteTextTemplate) => (
                        <div key={template._id} className="flex flex-col">
                            <div
                                onClick={() => openEdit(template)}
                                className={`cursor-pointer flex justify-between items-center border rounded ${activeId === template._id ? 'rounded-b-none bg-gray-50 dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-900'}`}
                            >
                                <div className="text-gray-900 dark:text-gray-100 p-2 flex flex-row items-center space-x-1">
                                    <Icon type="notes" />
                                    <h3 className="text-sm font-medium">{template.name}</h3>
                                    {template.isDefault && (
                                        <span className="text-xs text-indigo-500 ml-1">
                                            ({t('quote_text_templates.default_badge')})
                                        </span>
                                    )}
                                </div>
                                <Icon type={activeId === template._id ? 'up' : 'down'} className="mr-2" />
                            </div>
                            {activeId === template._id && (
                                <div className="border border-t-0 rounded-b p-3 space-y-3 bg-white dark:bg-gray-800 text-left">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                            {t('quote_text_templates.name')}
                                        </label>
                                        <input
                                            type="text"
                                            value={draftName}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                            {t('quote_text_templates.body')}
                                        </label>
                                        <Wysiwyg
                                            value={draftBody}
                                            onChange={(value: string) => setDraftBody(value)}
                                            rows={8}
                                            minimal
                                            className="text-gray-900 dark:text-gray-100 sm:text-sm"
                                        />
                                    </div>
                                    <label className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                                        <input
                                            type="checkbox"
                                            checked={draftIsDefault}
                                            onChange={(e) => setDraftIsDefault(e.target.checked)}
                                        />
                                        {t('quote_text_templates.is_default')}
                                    </label>
                                    <div className="flex justify-between gap-2">
                                        <Button
                                            title={t('common.delete') || ''}
                                            icon="delete"
                                            color="danger"
                                            small
                                            onClick={() => setToDelete(template._id)}
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                title={t('common.cancel') || ''}
                                                color="navigate"
                                                small
                                                onClick={() => setActiveId(null)}
                                            />
                                            <Button
                                                title={t('common.save') || ''}
                                                icon={saving ? 'loading' : 'save'}
                                                color="primary"
                                                small
                                                loading={saving}
                                                onClick={() => saveActive()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    <div className="flex flex-row items-center space-x-0 border bg-blue-100 dark:bg-blue-900/40 rounded">
                        <label className="max-w-[200px] flex-1 flex items-center text-sm px-2">
                            {t('quote_text_templates.add')}
                        </label>
                        <div className="flex-1">
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter')
                                        createTemplate();
                                }}
                                className="block w-full border-0 bg-transparent py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-0"
                                placeholder={t('quote_text_templates.name_placeholder') || ''}
                            />
                        </div>
                        <Button
                            title=""
                            icon={creating ? 'loading' : 'plus'}
                            color="primary"
                            small
                            loading={creating}
                            onClick={() => createTemplate()}
                            className="m-1"
                        />
                    </div>
                </div>
            )}

            <Confirm
                visible={!!toDelete}
                onCancel={() => setToDelete(null)}
                onConfirm={removeTemplate}
                title={t('quote_text_templates.confirm_delete') || ''}
                iconType="warning"
            />
        </div>
    );
}

export { QuoteTextTemplatesManager };
export default QuoteTextTemplatesManager;
